import { useId, type ReactElement, type ReactNode } from 'react';

type InfoTooltipProps = {
  readonly label: string;
  readonly children: ReactNode;
};

/**
 * A rule the interface follows that a reader could not otherwise infer, parked
 * behind a mark rather than spelled out in the layout. Focusable, so it is not
 * hover-only.
 */
export const InfoTooltip = ({ label, children }: InfoTooltipProps): ReactElement => {
  const tipId = useId();

  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={label}
        aria-describedby={tipId}
        className="border-line text-muted hover:text-content flex size-4 items-center justify-center rounded-pill border text-[10px] leading-none font-semibold normal-case"
      >
        {/* Optical, not geometric: the ink of a lowercase i sits high in its line box. */}
        <span className="block translate-y-[0.5px] leading-none">i</span>
      </button>
      <span
        id={tipId}
        role="tooltip"
        className="bg-surface border-subtle shadow-raised rounded-card text-secondary pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-60 -translate-x-1/2 border p-2.5 text-left text-xs leading-relaxed normal-case tracking-normal opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {children}
      </span>
    </span>
  );
};
